import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtDate, fmtMoney, fmtErr, todayLocal, photoSrc } from "../../api";
import Layout from "../../components/Layout";
import { Plus, Buildings, Lightning } from "@phosphor-icons/react";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "../../components/ui/carousel";
import { toast } from "sonner";

const COVER = "https://images.unsplash.com/photo-1458245201577-fc8a130b8829?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHw0fHxsYXduJTIwbW93ZXIlMjBmcmVzaCUyMGdyYXNzfGVufDB8fHx8MTc4NzQxMjE5NXww&ixlib=rb-4.1.0&q=85";

export default function OwnerHome() {
  const [props_, setProps] = useState(null);
  const [visits, setVisits] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [notes, setNotes] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [openVisit, setOpenVisit] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", access_notes: "", is_str: false });

  const load = () => {
    api.get("/properties").then((r) => setProps(r.data));
    api.get("/owner/visits").then((r) => setVisits(r.data));
    api.get("/owner/approvals").then((r) => setApprovals(r.data));
    api.get("/owner/renewals").then((r) => setRenewals(r.data));
    api.get("/owner/observations").then((r) => setNotes(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load by design
  useEffect(() => { load(); }, []);

  const confirmRenewal = async (id) => {
    try {
      const r = await api.post(`/agreements/${id}/confirm-renewal`);
      toast.success(`Renewed — next season starts ${r.data.next_season_start}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const addProperty = async (e) => {
    e.preventDefault();
    try {
      await api.post("/properties", { name: form.name, address: form.address, access_notes: form.access_notes, is_str: form.is_str });
      toast.success("Property added — your operator will set up services for it");
      setShowAdd(false);
      setForm({ name: "", address: "", access_notes: "", is_str: false });
      load();
    } catch (e2) { toast.error(fmtErr(e2)); }
  };

  const today = todayLocal();
  const upcoming = visits.filter((v) => v.date >= today && ["scheduled", "assigned", "in_progress"].includes(v.status)).sort((a, b) => a.date.localeCompare(b.date));
  const history = visits.filter((v) => ["completed", "invoiced", "paid"].includes(v.status));

  return (
    <Layout title="My properties">
      {renewals.map((r) => (
        <div key={r.id} className="card-flat mb-4 border-l-4 border-l-primary p-4" data-testid={`renewal-${r.id}`}>
          <p className="font-semibold">Renew {r.property_name} for next season?</p>
          <p className="text-sm text-muted-foreground">{r.services.map((s) => s.name).join(", ")} · season {r.season_start}–{r.season_end}</p>
          <button data-testid={`confirm-renewal-${r.id}`} onClick={() => confirmRenewal(r.id)} className="mt-3 h-12 w-full rounded-md bg-primary font-bold text-primary-foreground">Confirm renewal</button>
        </div>
      ))}
      {approvals.length > 0 && (
        <Link to="/owner/requests" data-testid="approvals-alert">
          <div className="mb-4 rounded-md bg-[hsl(43,74%,50%)] p-4 font-bold text-[hsl(240,10%,10%)]">
            {approvals.length} quote{approvals.length > 1 ? "s" : ""} waiting for your approval →
          </div>
        </Link>
      )}
      <Link to="/owner/requests/new" data-testid="new-request-btn">
        <div className="mb-3 flex h-14 items-center justify-center gap-2 rounded-md bg-primary font-bold text-primary-foreground">
          <Plus size={20} weight="bold" /> Request extra work
        </div>
      </Link>
      <Link to="/owner/book" data-testid="book-service-btn">
        <div className="mb-3 flex h-14 items-center justify-center gap-2 rounded-md border-2 border-primary font-bold text-primary">
          <Lightning size={20} weight="bold" /> Book a service — see open days
        </div>
      </Link>
      <button data-testid="add-property-btn" onClick={() => setShowAdd(!showAdd)}
        className="mb-6 flex h-14 w-full items-center justify-center gap-2 rounded-md border-2 border-primary font-bold text-primary">
        <Buildings size={20} weight="bold" /> Add a property
      </button>
      {showAdd && (
        <form onSubmit={addProperty} className="card-flat mb-6 space-y-3 p-4" data-testid="owner-add-property-form">
          <p className="text-sm text-muted-foreground">Add a property you'd like serviced — your operator gets it on their list right away.</p>
          <input data-testid="owner-prop-name" className="field-input w-full" placeholder="Property name (e.g. Lakeside Cottage)" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <input data-testid="owner-prop-address" className="field-input w-full" placeholder="Address" value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })} required />
          <input data-testid="owner-prop-access" className="field-input w-full" placeholder="Access notes (gate codes, pets…)" value={form.access_notes}
            onChange={(e) => setForm({ ...form, access_notes: e.target.value })} />
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input data-testid="owner-prop-str" type="checkbox" checked={form.is_str} onChange={(e) => setForm({ ...form, is_str: e.target.checked })} className="h-5 w-5" /> Short-term rental
          </label>
          <button data-testid="owner-prop-save" className="btn-primary-big !h-14 !text-base">Add property</button>
        </form>
      )}
      {props_?.length === 0 && !showAdd && (
        <div className="card-flat mb-6 border-l-4 border-l-primary p-4" data-testid="no-properties-prompt">
          <p className="font-semibold">No properties yet</p>
          <p className="text-sm text-muted-foreground">Tap "Add a property" above to get your first one serviced.</p>
        </div>
      )}

      <div className="mb-8 grid grid-cols-2 gap-3" data-testid="property-cards">
        {props_?.map((p) => (
          <div key={p.id} className="card-flat overflow-hidden" data-testid={`owner-property-${p.id}`}>
            <div className="h-24 bg-cover bg-center" style={{ backgroundImage: `url(${COVER})` }} />
            <div className="p-3">
              <p className="font-semibold leading-tight">{p.name}</p>
              <p className="text-xs text-muted-foreground">{p.address}</p>
              {p.is_str && <span className="mt-1 inline-block rounded-full bg-secondary px-2 py-0.5 text-xs font-bold text-secondary-foreground">Rental</span>}
            </div>
          </div>
        ))}
      </div>

      <section className="mb-8">
        <p className="label-caps mb-3">Upcoming visits</p>
        <div className="space-y-2" data-testid="upcoming-visits">
          {upcoming.length === 0 && <p className="card-flat p-6 text-sm text-muted-foreground">No visits scheduled.</p>}
          {upcoming.slice(0, 8).map((v) => (
            <div key={v.id} className="card-flat p-4" data-testid={`owner-visit-${v.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-semibold">{v.property_name}</p>
                <p className="text-sm font-bold text-primary">{fmtDate(v.date)}</p>
              </div>
              <p className="text-sm text-muted-foreground">{v.services.map((s) => s.name).join(", ") || "Add-on work"}</p>
              {v.instructions?.length > 0 && <p className="mt-1 text-xs font-semibold text-[hsl(43,74%,40%)]">Includes your special request</p>}
            </div>
          ))}
        </div>
      </section>

      <section>
        <p className="label-caps mb-3">Job history</p>
        <div className="space-y-2" data-testid="job-history">
          {history.length === 0 && <p className="card-flat p-6 text-sm text-muted-foreground">No completed jobs yet.</p>}
          {history.slice(0, 10).map((v) => (
            <div key={v.id} className="card-flat p-4">
              <button data-testid={`history-row-${v.id}`} onClick={() => setOpenVisit(openVisit === v.id ? null : v.id)} className="flex w-full items-center justify-between text-left">
                <div>
                  <p className="font-semibold">{v.property_name}</p>
                  <p className="text-sm text-muted-foreground">{fmtDate(v.date)} · {v.services.map((s) => s.name).join(", ") || "Add-on work"}</p>
                </div>
                <span className="text-xs font-bold uppercase text-primary">{v.status}{v.photos?.length > 0 ? ` · ${v.photos.length} photo${v.photos.length === 1 ? "" : "s"}` : ""}</span>
              </button>
              {openVisit === v.id && (
                <div className="mt-3 space-y-3 border-t border-border pt-3" data-testid={`history-detail-${v.id}`}>
                  {v.completion_notes && <p className="text-sm text-muted-foreground">{v.completion_notes}</p>}
                  {v.photos?.length > 0 ? (
                    <Carousel className="w-full" data-testid={`photo-gallery-${v.id}`}>
                      <CarouselContent>
                        {v.photos.map((p, i) => (
                          <CarouselItem key={`${i}-${p.length}`}>
                            <img src={photoSrc(p)} alt={`Completion photo ${i + 1}`} className="h-64 w-full rounded-md object-cover" />
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      {v.photos.length > 1 && (
                        <>
                          <CarouselPrevious className="left-2" data-testid={`gallery-prev-${v.id}`} />
                          <CarouselNext className="right-2" data-testid={`gallery-next-${v.id}`} />
                        </>
                      )}
                    </Carousel>
                  ) : (
                    <p className="text-xs text-muted-foreground">No photos attached to this visit.</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
      {notes.length > 0 && (
        <section className="mt-8">
          <p className="label-caps mb-3">Property notes from the crew</p>
          <div className="space-y-2" data-testid="property-notes">
            {notes.map((o) => (
              <div key={o.id} className="card-flat space-y-2 p-4" data-testid={`property-note-${o.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold">{o.property_name}</p>
                  <span className="text-xs font-bold text-muted-foreground">{fmtDate(o.owner_notified_at?.slice(0, 10))}</span>
                </div>
                <p className="text-sm">{o.description}</p>
                {o.triage_note && <p className="rounded-md bg-muted p-2 text-xs">{o.triage_note}</p>}
                {o.photos?.length > 0 && <div className="flex flex-wrap gap-2">{o.photos.map((p, i) => <img key={`${i}-${p.length}`} src={photoSrc(p)} alt="" className="h-16 w-16 rounded-md object-cover" />)}</div>}
              </div>
            ))}
          </div>
        </section>
      )}
    </Layout>
  );
}
